// Uploads for the question bank: images (question, option, passage) and a
// passage's PDF. The server checks the bytes and the size again
// (vc_be services/quizMediaService); these checks only save a wasted upload.

import apiClient from "../../utils/apiClient";
import { shrinkImageToFit } from "../../utils/shrinkImage";

export const IMAGE_MAX_BYTES = 1024 * 1024;
export const PDF_MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const IMAGE_ACCEPT = IMAGE_TYPES.join(",");

export const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const serverMessage = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * Upload one image. Resolves to { url, resized, size } — `resized` tells the
 * caller to say so — or throws an Error whose message is for the author.
 */
export async function uploadQuizImage(file) {
  if (!file) throw new Error("Choose an image to upload.");
  if (!IMAGE_TYPES.includes(file.type)) throw new Error("Upload a PNG, JPG, WebP or GIF image.");

  let toSend = file;
  let resized = false;
  if (file.size > IMAGE_MAX_BYTES) {
    let smaller = null;
    try { smaller = await shrinkImageToFit(file, IMAGE_MAX_BYTES); } catch { smaller = null; }
    if (!smaller) {
      throw new Error(`That image is ${formatBytes(file.size)} — the limit is 1 MB. Resize or compress it and try again.`);
    }
    toSend = smaller;
    resized = true;
  }

  const form = new FormData();
  form.append("file", toSend);
  try {
    const { data } = await apiClient.post("/quiz-questions/media/image", form);
    return { url: data.url, size: data.size, resized };
  } catch (err) {
    throw new Error(serverMessage(err, "The upload failed. Please try again."));
  }
}

/** Upload a passage PDF. Resolves to { key, name, size }. */
export async function uploadPassagePdf(file) {
  if (!file) throw new Error("Choose a PDF to upload.");
  const looksPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!looksPdf) throw new Error("Upload the document as a PDF.");
  if (file.size > PDF_MAX_BYTES) {
    throw new Error(`That PDF is ${formatBytes(file.size)} — the limit is 10 MB. Split it, or save it with smaller images.`);
  }
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await apiClient.post("/quiz-passages/upload", form);
    return data.file;
  } catch (err) {
    throw new Error(serverMessage(err, "The upload failed. Please try again."));
  }
}
